import * as React from 'react';
import { useTranslation } from 'react-i18next';

// Utils
import { getUserFullname } from '@src/shared/src/util/users';
// Constants
import { MIN_LONG_HAUL_DISTANCE, MIN_MEDIUM_HAUL_DISTANCE } from '@src/shared/src/const/app';
// Models
import { FlightTravelPolicyModel, PassengerModel } from '@src/shared/src/models';
// Components
import { SidebarSection } from '@toolkit/ui';
import Badge from '@toolkit/ui/Badge';
import { ArrowBackIcon } from '@toolkit/ui/icons';
import PolicyMaxPriceRow from './PolicyMaxPriceRow';
import HotelPolicyRegionTitle from './HotelPolicyRegionTitle';
import HotelPolicyStars from './HotelPolicyStars';
// Styles
import '../styles/SearchSideBarTravelPolicies.scss';

type Props = {
  passenger: PassengerModel;
  currentHaul?: string;
  onBack: () => void;
};

const SearchSideBarTravelPolicies: React.FC<Props> = ({ passenger, currentHaul, onBack }) => {
  const { t } = useTranslation();

  const flightPoliciesByHaul = passenger.flightTravelPolicies.reduce<
    Record<string, FlightTravelPolicyModel[]>
  >((acc, policy) => {
    (acc[policy.haul] ??= []).push(policy);
    return acc;
  }, {});

  return (
    <div className="sidebar-travel-policies hidden-xs-down">
      <div className="sidebar-travel-policies-header">
        <button type="button" className="sidebar-travel-policies-back" onClick={onBack}>
          <ArrowBackIcon />
        </button>
        <div className="sidebar-travel-policies-title">
          {t('SearchSideBarTravelPolicies.title', { name: getUserFullname(passenger) })}
        </div>
      </div>

      <div className="sidebar-inner">
        {passenger.flightTravelPolicies.length > 0 && (
          <SidebarSection title={t('global.flight')} icon="flight" isOpen>
            {Object.entries(flightPoliciesByHaul).map(([haul, policies]) => (
              <div key={`haul-${haul}`} className="sidebar-travel-policies-card">
                <div className="sidebar-travel-policies-haul-title">
                  <span>{t(`global.${haul}`)}</span>
                  {currentHaul === haul && (
                    <Badge
                      variant="attention"
                      label={t('SearchSideBarTravelPolicies.label.yourSearch')}
                    />
                  )}
                </div>
                <div className="sidebar-travel-policies-haul-range">
                  {t(`SearchSideBarTravelPolicies.haulRange.${haul}`, {
                    min: MIN_MEDIUM_HAUL_DISTANCE / 1000,
                    max: MIN_LONG_HAUL_DISTANCE / 1000,
                  })}
                </div>
                {policies.map((policy) => (
                  <PolicyMaxPriceRow
                    key={`flight-class-${haul}-${policy.comfortClass}`}
                    label={t(`global.${policy.comfortClass}`)}
                    value={policy.maxPrice}
                  />
                ))}
              </div>
            ))}
            <div className="sidebar-travel-policies-footnote">
              {t('SearchSideBarTravelPolicies.text.pricesPerPersonPerDirection')}
            </div>
          </SidebarSection>
        )}

        {passenger.trainTravelPolicies.length > 0 && (
          <SidebarSection title={t('global.train')} icon="directions_subway" isOpen>
            <div className="sidebar-travel-policies-card">
              {passenger.trainTravelPolicies.map((policy) => (
                <PolicyMaxPriceRow
                  key={`train-class-${policy.comfortClass}`}
                  label={t(`global.${policy.comfortClass}`)}
                  value={policy.maxPrice}
                />
              ))}
            </div>

            <div className="sidebar-travel-policies-footnote">
              {t('SearchSideBarTravelPolicies.text.pricesPerPersonPerDirection')}
            </div>
          </SidebarSection>
        )}

        {passenger.hotelTravelPolicies.length > 0 && (
          <SidebarSection title={t('global.hotel')} icon="hotel" isOpen>
            {passenger.hotelTravelPolicies.map((policy) => (
              <div key={`hotel-policy-${policy.id}`} className="sidebar-travel-policies-card">
                <HotelPolicyRegionTitle policy={policy} />
                <HotelPolicyStars maxStars={policy.maxStars} />

                <PolicyMaxPriceRow
                  label={t('SearchSideBarTravelPolicies.hotel.maxPrice')}
                  value={policy.maxPrice}
                />

                {!!policy.maxPriceWithBreakfast && (
                  <PolicyMaxPriceRow
                    label={t('SearchSideBarTravelPolicies.hotel.maxPriceWithBreakfast')}
                    value={policy.maxPriceWithBreakfast}
                  />
                )}
              </div>
            ))}

            <div className="sidebar-travel-policies-footnote">
              {t('SearchSideBarTravelPolicies.text.pricesPerPersonPerNight')}
            </div>
          </SidebarSection>
        )}

        {passenger.rentalTravelPolicies.length > 0 && (
          <SidebarSection title={t('global.rental')} icon="directions_car" isOpen>
            {passenger.rentalTravelPolicies.map((policy) => (
              <div key={`rental-policy-${policy.id}`} className="sidebar-travel-policies-card">
                <div className="sidebar-travel-policies-fare-row">
                  <span className="sidebar-travel-policies-fare-label">
                    {t('SearchSideBarTravelPolicies.rental.category')}
                  </span>
                  <span className="sidebar-travel-policies-fare-price">
                    {t(`carCategories.${policy.carCategory}`)}
                  </span>
                </div>
                <PolicyMaxPriceRow
                  label={t('SearchSideBarTravelPolicies.rental.maxPrice')}
                  value={policy.maxPrice}
                />
              </div>
            ))}

            <div className="sidebar-travel-policies-footnote">
              {t('SearchSideBarTravelPolicies.text.pricesPerDay')}
            </div>
          </SidebarSection>
        )}
      </div>
    </div>
  );
};

export default SearchSideBarTravelPolicies;
